<script>
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    toast
} from '@zerodevx/svelte-toast';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import {
    saveAs
} from 'file-saver';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import { activateSubscription } from "@app/lib/activateSubscription.js";
import * as m from "@app/paraglide/messages.js";
import { Document, Packer } from "docx";

import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import ccslTemplates from "@app/lib/ccsl/templates/index.js";
import { assembleSections } from "@app/lib/ccsl/sections.js";

let exporting = false,
    documentTitle = $state(projectState.name),
    ccslTemplate = $state(localStorage.getItem("cc-ccsl-template") || "default"),
    doc,
    dialogueEventGroupId = $state(eventGroupState.length > 0 ? eventGroupState[0].id : null),
    spottingEventGroupId = $state(eventGroupState.length > 0 ? eventGroupState[0].id : null);

if (authState.status === "in_trial") {
    showTrialWarning();
}

function showTrialWarning() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.ccsl_export_trial_title(),
        text: m.ccsl_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.ccsl_export_trial_activate(),
        denyButtonText: m.ccsl_export_trial_continue(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.trial,
    }).then((result) => {
        if (result.isConfirmed) {
            activateSubscription();
        }
    }).catch(err => {
        console.log(err);
        console.log(err.message)
    });
}

async function exportCcsl() {
    try {
        if (exporting) return;
        exporting = true;

        // Resolve the selected template
        const template = ccslTemplates[ccslTemplate];
        if (!template) {
            throw new Error(`Unknown CCSL template: ${ccslTemplate}`);
        }

        const templateConfig = template.getDocumentConfig();

        // Project context passed to template methods
        const project = {
            incode: projectState.incode,
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
            username: projectState.username,
        };

        // Build table header rows
        let tableRows = [...template.getHeaders()];

        let fileName = m.ccsl_export_file_name({
            title: documentTitle,
            template: ccslTemplate
        });

        // Shot Change Event Group
        let dialogueEventGroup = eventGroupState.find(eventGroup => eventGroup.id === dialogueEventGroupId);
        // Subtitle Event Group
        let spottingEventGroup = eventGroupState.find(eventGroup => eventGroup.id === spottingEventGroupId);

        if (!dialogueEventGroup || !spottingEventGroup) {
            throw new Error(m.ccsl_export_event_group_not_found());
        }

        dialogueEventGroup = JSON.parse(JSON.stringify(dialogueEventGroup));
        spottingEventGroup = JSON.parse(JSON.stringify(spottingEventGroup));

        if (authState.status === "in_trial") {
            dialogueEventGroup = replaceTrialText(dialogueEventGroup);
            spottingEventGroup = replaceTrialText(spottingEventGroup);
        }

        // Build data rows for each dialogue event
        dialogueEventGroup.events.forEach((event, index) => {
            // Find spotting events whose start falls within this dialogue event's time range
            let spottingEvents = spottingEventGroup.events.filter(
                spottingEvent => spottingEvent.start >= event.start && spottingEvent.start < event.end
            );

            let rows;
            if (spottingEvents.length > 0) {
                rows = template.buildDialogueRowWithSpotting(
                    event, index, spottingEvents, spottingEventGroup, project
                );
            } else {
                rows = template.buildDialogueRowWithoutSpotting(event, index, project);
            }

            tableRows.push(...rows);
        });

        // Assemble document sections based on template config
        const sections = assembleSections(
            tableRows,
            templateConfig,
            documentTitle,
            project,
            authState.status
        );

        doc = new Document({
            creator: "Closed Caption Creator 8",
            sections
        });

        let fileBlob = await Packer.toBlob(doc);
        saveAs(fileBlob, fileName, {
            autoBom: true
        });

        toast.push(m.ccsl_export_complete_toast(), {
            classes: ["toast-success"]
        });
    } catch (error) {
        console.log(error);
    } finally {
        //Save ccslTemplate selection to local storage
        localStorage.setItem("cc-ccsl-template", ccslTemplate);
        hideModal();
    }
}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'ccslExport' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="ccslExportTitle"
    aria-describedby="ccslExportDescription"
    tabindex="-1" 
    id="CcslExportModal"
>
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="ccslExportTitle">{m.ccsl_export_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.ccsl_export_close_label()}
                    id="closeCcslExportBtn"
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                <p id="ccslExportDescription" class="sr-only">{m.ccsl_export_description()}</p>
                <form>
                    <div class="mb-3">
                        <label class="form-label" for="ccslDocTitle">{m.ccsl_export_document_title()}</label>
                        <input 
                            id="ccslDocTitle"
                            class="form-control" 
                            type="text" 
                            placeholder={m.ccsl_export_document_title_placeholder()} 
                            bind:value={documentTitle}
                            aria-describedby="ccslDocTitleHelp"
                        >
                        <span id="ccslDocTitleHelp" class="visually-hidden">{m.ccsl_export_document_title_help()}</span>
                    </div>

                    <div class="mb-3">
                        <label class="form-label" for="ccslTemplate">{m.ccsl_export_template_label()}</label>
                        <select 
                            id="ccslTemplate"
                            class="form-select" 
                            bind:value={ccslTemplate}
                            aria-describedby="ccslTemplateHelp"
                        >
                            <option value="default">{m.ccsl_export_template_default()}</option>
                            <option value="condensed">{m.ccsl_export_template_condensed()}</option>
                            <option value="style5A">{m.ccsl_export_template_style_5a()}</option>
                            <option value="technicolor">{m.ccsl_export_template_technicolor()}</option>
                            <option value="starter">{m.ccsl_export_template_starter()}</option>
                        </select>
                        <span id="ccslTemplateHelp" class="visually-hidden">{m.ccsl_export_template_help()}</span>
                    </div>

                    <p class="lead text-warning">{m.ccsl_export_event_groups()}</p>
                    <div class="mb-3">
                        <label class="form-label" for="DialogueEventGroupSelect">{m.ccsl_export_dialogue_event_group_label()} <i class="bi bi-info-circle" title={m.ccsl_export_dialogue_event_group_help()}></i></label>
                        <select class="form-select" id="DialogueEventGroupSelect" bind:value={dialogueEventGroupId}>
                            {#each eventGroupState as eventGroup (eventGroup.id)}
                            <option value={eventGroup.id}>{eventGroup.name}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="SpottingListEventGroupSelect">{m.ccsl_export_spotting_event_group_label()} <i class="bi bi-info-circle" title={m.ccsl_export_spotting_event_group_help()}></i></label>
                        <select class="form-select" id="SpottingListEventGroupSelect" bind:value={spottingEventGroupId}>
                            {#each eventGroupState as eventGroup (eventGroup.id)}
                            <option value={eventGroup.id}>{eventGroup.name}</option>
                            {/each}
                        </select>
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                <button 
                    class="btn btn-primary" 
                    type="button" 
                    id="exportCcslBtn"
                    aria-label={m.ccsl_export_export_button()}
                    onclick={exportCcsl}
                >{m.ccsl_export_export_button()}</button>
            </div>
        </div>
    </div>
</div>
